import type { User } from "firebase/auth";
import { useEffect, useState } from "react";
import * as api from "./api";
import { Chevron, Dropdown } from "./Menu";
import { switchable, switchHref } from "./orgs";

/** The organizations where the user has a role, or none until the API answers. */
export function useOrganizations(user: User): api.Organization[] {
  const [found, setFound] = useState<api.Organization[]>([]);
  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const listed = await api.organizations(await user.getIdToken());
        if (live) setFound(listed);
      } catch {
        // Without the list, the header shows only the organization name.
      }
    })();
    return () => {
      live = false;
    };
  }, [user]);
  return switchable(found);
}

const readLocation = () => ({ path: location.pathname, hash: location.hash });

function useLocation(): { path: string; hash: string } {
  const [where, setWhere] = useState(readLocation);
  useEffect(() => {
    const update = () => setWhere(readLocation());
    addEventListener("popstate", update);
    addEventListener("hashchange", update);
    return () => {
      removeEventListener("popstate", update);
      removeEventListener("hashchange", update);
    };
  }, []);
  return where;
}

function OrgLinks({ orgs }: { orgs: api.Organization[] }) {
  const { path, hash } = useLocation();
  return (
    <ul className="org-list">
      {orgs.map((org) => (
        <li key={org.slug}>
          {org.current ? (
            <span aria-current="true" data-testid="org-current">
              {org.name} (open now)
            </span>
          ) : (
            <a
              href={switchHref(org.slug, path, hash)}
              data-testid="org-link"
              data-org={org.slug}
              onClick={() => console.info("[org] switch", { from: orgs.find((o) => o.current)?.slug, to: org.slug })}
            >
              {org.name}
            </a>
          )}
        </li>
      ))}
    </ul>
  );
}

/** The organization name at the left of the header; with two or more organizations it opens the list to switch (rule U-9). */
export function OrgSwitcher({ user, name }: { user: User; name: string }) {
  const orgs = useOrganizations(user);
  if (!orgs.length)
    return (
      <strong className="org-name" data-testid="org-name">
        {name}
      </strong>
    );
  return (
    <Dropdown
      className="org-switch"
      testId="org-menu"
      label={`${name}. Switch the organization`}
      summary={
        <>
          <strong className="org-name" data-testid="org-name">
            {name}
          </strong>
          <Chevron open />
        </>
      }
    >
      {() => (
        <nav aria-label="Organizations">
          <p className="dropdown-title">Switch the organization</p>
          <OrgLinks orgs={orgs} />
        </nav>
      )}
    </Dropdown>
  );
}

/** The same list on the Settings page (rule U-9). */
export function OrgSettings({ user }: { user: User }) {
  const orgs = useOrganizations(user);
  if (!orgs.length) return null;
  return (
    <div data-testid="org-settings">
      <p>Your organizations. A link opens the same page there.</p>
      <OrgLinks orgs={orgs} />
    </div>
  );
}
